import { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { createProduct, updateProduct, deleteProduct, uploadDocument, uploadImage } from '../api/api';
import AdminLayout from './AdminLayout';
import './Admin.css';

function ProductModal({ product, categories, onClose, onSave, allProducts }) {
  // Migrate old img format to images array
  const initialFormData = product ? {
    ...product,
    images: product.images || (product.img ? [product.img] : ['']),
    productCode: product.productCode || '',
    features: product.features || [],
    measurements: product.measurements || {},
    guarantees: product.guarantees || [],
    installation: product.installation || '',
    documents: product.documents || [],
    suggestedProducts: product.suggestedProducts || []
  } : {
    name: '',
    category: '',
    subtitle: '',
    description: '',
    tags: [],
    finish: '',
    width: '',
    length: '',
    pile: '',
    weight: '',
    density: '',
    rollWidth: '',
    suitability: '',
    images: [''],
    accent: '#c2a87a',
    productCode: '',
    features: [],
    measurements: {},
    guarantees: [],
    installation: '',
    documents: [],
    suggestedProducts: []
  };

  const [formData, setFormData] = useState(initialFormData);
  const [tagInput, setTagInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  const [guaranteeInput, setGuaranteeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState({});
  const [uploadingImage, setUploadingImage] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageChange = (index, value) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      newImages[index] = value;
      return { ...prev, images: newImages };
    });
  };

  const handleAddImage = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAddFeature = (e) => {
    if (e.key === 'Enter' && featureInput.trim()) {
      e.preventDefault();
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleAddGuarantee = (e) => {
    if (e.key === 'Enter' && guaranteeInput.trim()) {
      e.preventDefault();
      setFormData(prev => ({
        ...prev,
        guarantees: [...prev.guarantees, guaranteeInput.trim()]
      }));
      setGuaranteeInput('');
    }
  };

  const handleRemoveGuarantee = (index) => {
    setFormData(prev => ({
      ...prev,
      guarantees: prev.guarantees.filter((_, i) => i !== index)
    }));
  };

  const handleMeasurementChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      measurements: { ...prev.measurements, [key]: value }
    }));
  };

  const handleAddDocument = () => {
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, { name: '', url: '' }]
    }));
  };

  const handleDocumentChange = (index, field, value) => {
    setFormData(prev => {
      const newDocuments = [...prev.documents];
      newDocuments[index] = { ...newDocuments[index], [field]: value };
      return { ...prev, documents: newDocuments };
    });
  };

  const handleRemoveDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    const token = localStorage.getItem('adminToken');
    setUploadingDoc(prev => ({ ...prev, [index]: true }));

    try {
      const result = await uploadDocument(file, token);
      // Update the document URL with the uploaded file path
      handleDocumentChange(index, 'url', result.url);
      alert(`File "${result.filename}" uploaded successfully!`);
    } catch (error) {
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingDoc(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;

    const token = localStorage.getItem('adminToken');
    setUploadingImage(prev => ({ ...prev, [index]: true }));

    try {
      const result = await uploadImage(file, token);
      handleImageChange(index, result.url);
      alert(`Image "${result.filename}" uploaded successfully!`);
    } catch (error) {
      alert('Image upload failed: ' + error.message);
    } finally {
      setUploadingImage(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleSuggestedProductsChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
    setFormData(prev => ({ ...prev, suggestedProducts: selected }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert('Failed to save product: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Category *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Subtitle *</label>
            <input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              required
            />
          </div>
          <div className="form-group">
            <label>Tags</label>
            <div className="tags-input">
              {formData.tags.map(tag => (
                <span key={tag} className="tag">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag)}>&times;</button>
                </span>
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Type and press Enter"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Product Images *</label>
            {formData.images.map((imageUrl, index) => (
              <div key={index} style={{ marginBottom: '14px', padding: '12px', background: '#f9f7f2', borderRadius: '6px', border: '1px solid #ddd' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => handleImageChange(index, e.target.value)}
                    placeholder={index === 0 ? "Primary image URL or upload below *" : "Additional image URL or upload below"}
                    required={index === 0}
                    style={{ flex: 1 }}
                  />
                  {formData.images.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      style={{ padding: '8px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  )}
                </div>
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt={`Product preview ${index + 1}`}
                    style={{ width: '100%', maxWidth: '220px', height: '120px', objectFit: 'cover', display: 'block', marginBottom: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label
                    htmlFor={`image-file-${index}`}
                    style={{
                      padding: '8px 16px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploadingImage[index] ? 'not-allowed' : 'pointer',
                      opacity: uploadingImage[index] ? 0.6 : 1,
                      display: 'inline-block'
                    }}
                  >
                    {uploadingImage[index] ? 'Uploading...' : 'Upload Image'}
                  </label>
                  <input
                    id={`image-file-${index}`}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleImageUpload(index, file);
                        e.target.value = '';
                      }
                    }}
                    disabled={uploadingImage[index]}
                    style={{ display: 'none' }}
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>
                    JPG, PNG, WEBP or GIF. Max 6MB.
                  </small>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddImage}
              style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '4px' }}
            >
              + Add Another Image
            </button>
          </div>
          <div className="form-group">
            <label>Accent Color</label>
            <input
              type="color"
              name="accent"
              value={formData.accent}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Finish</label>
            <input
              type="text"
              name="finish"
              value={formData.finish || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Width</label>
            <input
              type="text"
              name="width"
              value={formData.width || ''}
              onChange={handleChange}
              placeholder="e.g., 192mm"
            />
          </div>
          <div className="form-group">
            <label>Length</label>
            <input
              type="text"
              name="length"
              value={formData.length || ''}
              onChange={handleChange}
              placeholder="e.g., 1380mm"
            />
          </div>
          <div className="form-group">
            <label>Suitability *</label>
            <input
              type="text"
              name="suitability"
              value={formData.suitability}
              onChange={handleChange}
              placeholder="e.g., Residential / Light Commercial"
              required
            />
          </div>

          <hr style={{ margin: '2rem 0', border: 'none', borderTop: '2px solid #ddd' }} />
          <h3 style={{ marginBottom: '1.5rem', color: '#0f1f30' }}>Product Details (Accordion Content)</h3>

          <div className="form-group">
            <label>Product Code</label>
            <input
              type="text"
              name="productCode"
              value={formData.productCode}
              onChange={handleChange}
              placeholder="e.g., LAM-CO-12-192"
            />
          </div>

          <div className="form-group">
            <label>Features</label>
            <div className="tags-input">
              {formData.features.map((feature, index) => (
                <span key={index} className="tag">
                  {feature}
                  <button type="button" onClick={() => handleRemoveFeature(index)}>&times;</button>
                </span>
              ))}
              <input
                type="text"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={handleAddFeature}
                placeholder="Type feature and press Enter"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Measurements (JSON object)</label>
            <textarea
              value={JSON.stringify(formData.measurements, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setFormData(prev => ({ ...prev, measurements: parsed }));
                } catch (err) {
                  // Invalid JSON, let user continue typing
                }
              }}
              rows="6"
              placeholder='e.g., { "thickness": "12mm", "width": "192mm", "coverage": "2.12m² per pack" }'
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </div>

          <div className="form-group">
            <label>Guarantees & Accreditations</label>
            <div className="tags-input">
              {formData.guarantees.map((guarantee, index) => (
                <span key={index} className="tag">
                  {guarantee}
                  <button type="button" onClick={() => handleRemoveGuarantee(index)}>&times;</button>
                </span>
              ))}
              <input
                type="text"
                value={guaranteeInput}
                onChange={(e) => setGuaranteeInput(e.target.value)}
                onKeyDown={handleAddGuarantee}
                placeholder="Type guarantee and press Enter"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Installation Instructions</label>
            <textarea
              name="installation"
              value={formData.installation}
              onChange={handleChange}
              rows="4"
              placeholder="Detailed installation instructions..."
            />
          </div>

          <div className="form-group">
            <label>Documents</label>
            {formData.documents.map((doc, index) => (
              <div key={index} style={{ marginBottom: '16px', padding: '12px', background: '#f9f7f2', borderRadius: '6px', border: '1px solid #ddd' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={doc.name}
                    onChange={(e) => handleDocumentChange(index, 'name', e.target.value)}
                    placeholder="Document name (e.g., Installation Guide)"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveDocument(index)}
                    style={{ padding: '8px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={doc.url}
                    onChange={(e) => handleDocumentChange(index, 'url', e.target.value)}
                    placeholder="Document URL or upload file below"
                    style={{ flex: 1 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                  <label
                    htmlFor={`file-${index}`}
                    style={{
                      padding: '8px 16px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploadingDoc[index] ? 'not-allowed' : 'pointer',
                      opacity: uploadingDoc[index] ? 0.6 : 1,
                      display: 'inline-block'
                    }}
                  >
                    {uploadingDoc[index] ? 'Uploading...' : '📎 Upload PDF'}
                  </label>
                  <input
                    id={`file-${index}`}
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload(index, file);
                        e.target.value = ''; // Reset input
                      }
                    }}
                    disabled={uploadingDoc[index]}
                    style={{ display: 'none' }}
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>
                    {uploadingDoc[index] ? 'Uploading...' : 'PDF only, max 10MB'}
                  </small>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddDocument}
              style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '4px' }}
            >
              + Add Document
            </button>
          </div>

          <div className="form-group">
            <label>Suggested Products (hold Ctrl/Cmd to select multiple)</label>
            <select
              multiple
              value={formData.suggestedProducts.map(String)}
              onChange={handleSuggestedProductsChange}
              style={{ minHeight: '120px' }}
            >
              {allProducts && allProducts.filter(p => p.id !== product?.id).map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} - {p.category}
                </option>
              ))}
            </select>
            <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
              Selected: {formData.suggestedProducts.length} product(s)
            </small>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminProducts({ onLogout }) {
  const { products, categories, refreshData } = useData();
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const token = localStorage.getItem('adminToken');

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleSaveProduct = async (productData) => {
    if (editingProduct) {
      await updateProduct(editingProduct.id, productData, token);
    } else {
      await createProduct(productData, token);
    }
    await refreshData();
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await deleteProduct(id, token);
      await refreshData();
    }
  };

  return (
    <AdminLayout onLogout={onLogout}>
      <div className="admin-header">
        <h1>Products</h1>
        <p>Manage your flooring products</p>
      </div>
      <div className="admin-actions">
        <button className="btn-add" onClick={handleAddProduct}>
          + Add New Product
        </button>
      </div>
      <div className="data-table">
        {/* Desktop Table View */}
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Subtitle</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id}>
                <td>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={product.images?.[0] || product.img} alt={product.name} />
                    {product.images && product.images.length > 1 && (
                      <span style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}>
                        {product.images.length}
                      </span>
                    )}
                  </div>
                </td>
                <td>{product.name}</td>
                <td>{product.category}</td>
                <td>{product.subtitle}</td>
                <td>
                  <div className="table-actions">
                    <button className="btn-edit" onClick={() => handleEditProduct(product)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDeleteProduct(product.id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="data-list">
        {products.map(product => (
          <div key={product.id} className="data-list-item">
            <div className="data-list-content">
              {/* Image on Left */}
              <div className="data-list-image">
                <div style={{ position: 'relative' }}>
                  <img src={product.images?.[0] || product.img} alt={product.name} />
                  {product.images && product.images.length > 1 && (
                    <span className="image-badge">
                      {product.images.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Details on Right */}
              <div className="data-list-details">
                <h3 className="data-list-title">{product.name}</h3>
                <div className="data-list-meta">
                  <span className="data-list-category">{product.category}</span>
                  <span className="data-list-separator">•</span>
                  <span className="data-list-subtitle">{product.subtitle}</span>
                </div>
              </div>
            </div>

            {/* Buttons Below */}
            <div className="data-list-actions">
              <button className="btn-edit" onClick={() => handleEditProduct(product)}>
                Edit Product
              </button>
              <button className="btn-delete" onClick={() => handleDeleteProduct(product.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      {showModal && (
        <ProductModal
          product={editingProduct}
          categories={categories}
          onClose={() => setShowModal(false)}
          onSave={handleSaveProduct}
          allProducts={products}
        />
      )}
    </AdminLayout>
  );
}
